import { useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

/**
 * MagneticButton — wraps any clickable element and pulls it toward the cursor
 * when nearby. Adds a sheen sweep on hover.
 *
 * Usage:
 *   <MagneticButton onClick={...}>
 *     <Button data-testid="...">Pay $9.99</Button>
 *   </MagneticButton>
 *
 * Props:
 *  - strength: how much the button follows the cursor (default 0.35)
 *  - radius:   max distance (in px) from button center to start magnetism (default 90)
 *  - sheen:    show the diagonal sheen sweep on hover (default true)
 *  - className: passthrough on the outer wrapper
 */
export default function MagneticButton({
  children,
  strength = 0.35,
  radius = 90,
  sheen = true,
  className = '',
  onClick,
  ...rest
}) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { damping: 18, stiffness: 220, mass: 0.4 });
  const sy = useSpring(y, { damping: 18, stiffness: 220, mass: 0.4 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist < radius) {
        x.set(dx * strength);
        y.set(dy * strength);
      } else {
        x.set(0); y.set(0);
      }
    };
    const onLeave = () => { x.set(0); y.set(0); };
    window.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [strength, radius, x, y]);

  return (
    <motion.div
      ref={ref}
      style={{ x: sx, y: sy }}
      className={`inline-block relative magnetic-btn ${className}`}
      onClick={onClick}
      data-magnetic="true"
      {...rest}
    >
      {sheen && <span aria-hidden className="magnetic-btn__sheen" />}
      {children}
    </motion.div>
  );
}
